/**
 * Filter menu: multi-screen menu to select a cohort or property filter
 */
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import cohortsClient from '../../../report/queries/app-api/cohorts-client';
import {pluralize, serializeAttributes} from '../../../util';
import {preventTracking, propagateEvent} from '../../../util/dom';
import {registerMPElement} from '../../../util/register-element';
import PropertyFilter from '../../../widgets/property-filter-menu/models/property-filter';
import {
  DisplayContext,
  ListOperator,
  PropertyType,
  ResourceType,
} from '../../../widgets/property-filter-menu/models/types';
import PropertyFilterFactory from '../../../widgets/property-filter-menu/models/utils';
import {ChildMenu} from '../child-menu';
import {PropertiesMenu, PropertyItem} from '../properties-menu/types';
import {CohortSegment} from '../segment-menu/types';

import {FilterMenuState} from './types';

import css from './index.styl?inline';
import template from './index.jade';
import {getCohortFromPropertyItem} from '../util';

// Terminology may not be/remain the same as Cohort operators so we remap
// the labels
const COHORT_FILTER_OPERATOR_LABELS = {
  [ListOperator.Contains]: `in`,
  [ListOperator.DoesNotContain]: `not in`,
};

// based on if the state variable stagedPropertyFilter is not, filter-menu can have one of the 2
// kinds of child nodes underneath: if stagedPropertyFilter is not set, we have properties-menu, for
// the user to pick a property, which gets set to stagedPropertyFilter, which pushes filter-menu to
// the next stage, with mp-property-values-screen as the child. we need to make sure properties-menu
// is the one under the filter-menu instance at all times except only when the filter-menu is open
// and the user has selected a property. If properties-menu is not the child not when the
// filter-menu is closed, for example, we miss any callbacks going to the properties-menu to update
// the state, leaving an inconsistent UI.
// TODO: the above makes the state keeping of filter-menu subtle and error-prone. Can we do better?
// Is having properties-menu always in the DOM (but not always visible) a viable option?
// TODO(chi): add tests for the different consistency cases of custom properties
export default registerMPElement(
  `mp-filter-menu`,
  class FilterMenu extends ChildMenu<FilterMenuState> {
    public tooltipPositionParentElement: HTMLElement;
    private propertiesMenu: PropertiesMenu;

    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'allow-cohort-filter': `boolean`,
        'enable-behavioral-properties': {
          type: `boolean`,
          default: false,
        },
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'display-context': {type: `string`, default: DisplayContext.Default},
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          cohorts: [],
          dataGroups: [],
          selectedCohortFilterOption: this.getCohortFilterOption(ListOperator.Contains),
          selectedFilterType: null,
          stagedPropertyFilter: null,
        },

        helpers: {
          ...super.config.helpers,
          serializeAttributes,
          preventTracking,

          getCohortFilterOptions: () => this.getCohortFilterOptions(),
          getTooltipPositionParentElement: () => this.tooltipPositionParentElement,
          hasSelectedPropertyFilter: () => this.hasSelectedPropertyFilter(),
          isCohortSelection: () => this.isOnCohortsTab(),
          onCohortFilterCommit: () => this.onCohortFilterSelected(),
          onMenuInserted: (vnode) => requestAnimationFrame(() => (this.propertiesMenu = vnode.elm)),
          onPropertyFilterBackClick: () => this.update({stagedPropertyFilter: null}),
          onPropertyFilterChange: (ev) => this.update({stagedPropertyFilter: ev.detail.propertyFilter}),
          onPropertyFilterCommit: (ev) => this.onPropertyFilterSelected(ev.detail.propertyFilter),

          /** Updates the staged cohort filter operator */
          onCohortFilterOptionChange: (ev) => this.update({selectedCohortFilterOption: ev.detail.value}),

          handleDeleteCustomProperty: (ev) => {
            ev.stopPropagation();
            propagateEvent(this, ev);
          },

          /** Triggers multi-select on the underlying mp-properties-menu */
          handlePropertySelectOption: (ev) => {
            const {stagedPropertyFilter} = this.state;
            this.propertiesMenu.useMultipleSelection = ev.detail.selected === ResourceType.Cohort;

            this.update({
              ...(!stagedPropertyFilter
                ? {selectedCohortFilterOption: this.getCohortFilterOption(ListOperator.Contains)}
                : {}),
            });
          },

          handleStageChange: () => this.update(),

          /** Stages the selected property or cohort */
          handlePropertySelect: (ev) => {
            const {item, rawItem, onItemClicked, commit} = ev.detail;
            const isCohort = Array.isArray(item) || item.resourceType === ResourceType.Cohort;

            // clear staged property filter if no cohorts are selected
            if (isCohort) {
              if (Array.isArray(item) && !item.length) {
                this.update({stagedPropertyFilter: null});
              } else {
                this.handleCohortSelect(item, rawItem, commit);
              }
            } else {
              this.handlePropertySelect(item, onItemClicked);
            }
          },

          /** Provides a selection value for the underlying menu */
          getMenuSelection: () => this.transformSelection(this.selected),

          /** Prevent commiting until cohort filter has selected items */
          isCohortFilterValid: () => {
            const {stagedPropertyFilter} = this.state;
            return (
              stagedPropertyFilter &&
              stagedPropertyFilter.resourceType === ResourceType.Cohort &&
              !isEmpty(stagedPropertyFilter.filterValue)
            );
          },
        },
      };
    }

    public connectedCallback() {
      super.connectedCallback();
      this.fetchCohorts();
      this.fetchDataGroups();
    }

    public attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);

      if (attr === `open`) {
        if (this.attr(`open`)) {
          this.updateFromAttributes();
        } else {
          this.update(omit(this.config.defaultState, `cohorts`, `dataGroups`));
        }
      }
    }

    get selected() {
      return this.attr(`open`) ? this.state.stagedPropertyFilter : this.state.selected;
    }

    public shouldCloseOnSelect(detail: any) {
      return !detail.keepMenuOpen;
    }

    /**
     * Sets the selection state from the selected filter
     */
    public updateFromAttributes() {
      const selected = this.attr(`selected`) || {};
      const selectionType = selected && selected.resourceType;
      const isCohort = selectionType === ResourceType.Cohort;
      const selectedCohortFilterOption = isCohort && this.getCohortFilterOption(selected.filterOperator);

      this.update({
        stagedPropertyFilter: selectionType ? new PropertyFilter(selected) : null,
        selectedCohortFilterOption: selectedCohortFilterOption || this.getCohortFilterOption(ListOperator.Contains),
      });
    }

    /**
     * Display search menu only when a type has been selected
     * and not in the property value screen
     */
    public isContentSearchable() {
      return !this.hasSelectedPropertyFilter() && !this.propertiesMenu.isInnerContentSearchable();
    }

    public transformSelectedVal(val) {
      return (val && new PropertyFilter(val)) || val;
    }

    public selectedIcon() {
      const {selected} = this.state;

      switch (selected && selected.resourceType) {
        case ResourceType.User:
          return selected.dataGroupId ? `group-attribute` : `profile-attribute`;
        case ResourceType.Event:
          return `event-attribute`;
        default:
          return null;
      }
    }

    public selectedLabel() {
      if (this.selected) {
        switch (this.selected.resourceType) {
          case ResourceType.Cohort:
            return this.getCohortFilterLabel(this.selected);
          case ResourceType.User:
          case ResourceType.Event:
            return this.getPropertyFilterLabel(this.selected);
          default:
            break;
        }
      }

      return `Select property...`;
    }

    private getCohortFilterLabel(filter) {
      const {filterValue, filterOperator} = filter;
      const cohortCount = filterValue.length;

      if (!cohortCount || this.attr(`open`)) {
        return `${this.state.selectedCohortFilterOption}...`;
      }

      const firstCohort = filter.filterValue[0].cohort;
      let cohortName = firstCohort.name;

      // for cases where cohort name was not stored, find it
      // in the list of fetched cohorts
      if (!cohortName) {
        const cohort = this.state.cohorts.find((item) => item.id === firstCohort.id);
        cohortName = (cohort && cohort.name) || `Loading...`;
      }

      const suffix = [
        cohortName,
        cohortCount > 1 ? ` or ${cohortCount - 1} ${pluralize(`other`, cohortCount - 1)}` : ``,
      ].join(``);
      const groupLabel = this.getCohortGroupLabel(this.attr(`data-group-id`));

      return `${groupLabel} ${COHORT_FILTER_OPERATOR_LABELS[filterOperator] || filterOperator} ${suffix}`;
    }

    private getPropertyFilterLabel(filter) {
      const labelParts = filter.getLabelParts();
      let filterValue = [];

      if (!this.attr(`open`)) {
        filterValue = labelParts
          .slice(2)
          .map((part) => ({label: part.label, type: part.highlight ? `primary` : `secondary`}));
      } else {
        filterValue = [{label: `...`, type: `primary`}];
      }

      return [
        {label: labelParts[0].label, type: `primary`},
        {label: labelParts[1].label, type: `secondary`},
        ...filterValue,
      ];
    }

    private handlePropertySelect(item: PropertyItem, onItemClicked) {
      const {customProperty, customPropertyId, dataGroupId, propertyDefaultType, resourceType, type, value} = item;
      // not all custom properties have a well-defined type because the formula could return
      // mixed-typed values. Use 'string' for those cases to hydrate the rest of the filter ui
      const {name, propertyType} = customProperty || {name: null, propertyType: PropertyType.String};
      const propertyFilterAttrs = {
        ...(customProperty ? {customProperty} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(dataGroupId ? {dataGroupId} : {}),
        resourceType,
        propertyDefaultType: propertyDefaultType || propertyType,
        propertyName: value || name,
        propertyType: type || propertyType,
      };
      if (this.attr(`open`)) {
        // only when we are open, do we set stagedPropertyFilter, to render mp-property-values-screen
        const stagedPropertyFilter = this.createPropertyFilter(propertyFilterAttrs);
        this.update({stagedPropertyFilter});
      }
      // we keep the new property (from properties-menu), merge with the values of the old
      // filter (from mp-property-values-screen), add a typecast to the new customProperty filter if
      // the type is different so it fits with the old filter values, to form a filter to bubble up.
      // when we get a *customProperty* *update* from the underlying properties-menu
      if (this.state.selected && (item.customProperty || item.customPropertyId) && !onItemClicked) {
        const currentPropertyFilter = this.createPropertyFilter({
          ...propertyFilterAttrs,
          ...{propertyType: this.state.selected.propertyType},
          ...pick(this.state.selected, [`propertyObjectKey`, `filterOperator`, `filterValue`]),
        });
        this.dispatchSelect(currentPropertyFilter, null, true);
      }
    }

    private handleCohortSelect(selection: PropertyItem | PropertyItem[], rawItem: PropertyItem, commit: any = false) {
      const isSingleSelection = !Array.isArray(selection);
      const items = isSingleSelection ? [selection as PropertyItem] : (selection as PropertyItem[]);

      const filterValue = items.map(getCohortFromPropertyItem);

      const filter = this.createCohortFilter({
        filterValue,
        filterOperator: this.state.selectedCohortFilterOption,
      });

      // when selecting a cohort item from the `All` tab
      // or inline cohort commit that filter selection
      if (isSingleSelection || commit) {
        this.dispatchSelect(filter.getAttributes(), rawItem);
      } else {
        this.update({stagedPropertyFilter: filter});
      }
    }

    private dispatchSelect(item, rawItem = null, keepMenuOpen = false) {
      const trackingProps = this.trackingProps(item, rawItem);
      this.dispatchEvent(new CustomEvent(`select`, {detail: {item, trackingProps, keepMenuOpen}}));
    }

    private onPropertyFilterSelected(filter: PropertyFilter) {
      this.dispatchSelect(filter.getAttributes());
    }

    private onCohortFilterSelected() {
      const {stagedPropertyFilter} = this.state;

      const filter = this.createCohortFilter({
        ...stagedPropertyFilter.getAttributes(),
        filterOperator: this.state.selectedCohortFilterOption,
      });

      this.dispatchSelect(filter.getAttributes());
    }

    private hasSelectedPropertyFilter() {
      const {stagedPropertyFilter} = this.state;
      return stagedPropertyFilter && stagedPropertyFilter.propertyName;
    }

    private async fetchCohorts() {
      if (!this.attr(`project-has-cohorts`)) {
        return;
      }
      const workspaceId = this.attr(`workspace-id`);
      const params = workspaceId ? {workspaceId} : {projectID: this.attr(`project-id`)};
      let cohorts = cohortsClient.getCachedResponse(params);

      if (!cohorts) {
        try {
          cohorts = await cohortsClient.query(params);
        } catch (err) {
          cohorts = [];
          console.error(`Error loading cohorts`, err);
        }
      }

      this.update({cohorts});
    }

    private async fetchDataGroups() {
      if (!this.attr(`all-data-groups`)) {
        return;
      }
      const dataGroups = await this.retrieveDataGroups();
      this.update({dataGroups});
    }

    private isOnCohortsTab() {
      return this.propertiesMenu && this.propertiesMenu.getSelectedTab() === ResourceType.Cohort;
    }

    /**
     * Transforms the selected format to
     * the format used in mp-properties-menu
     */
    private transformSelection(selection) {
      const selectionType = selection && selection.resourceType;

      switch (selectionType) {
        case ResourceType.Cohort:
          return selection.filterValue.map((entry) => ({
            resourceType: ResourceType.Cohort,
            value: entry.cohort.id || {cohort: entry.cohort},
            label: entry.cohort.name,
          }));
        case ResourceType.User:
        case ResourceType.Event: {
          const propertyType =
            selection.propertyType || (selection.customProperty && selection.customProperty.propertyType);
          return {
            ...(selection.customPropertyId ? {customPropertyId: selection.customPropertyId} : null),
            ...(selection.customProperty ? {customProperty: selection.customProperty} : null),
            dataGroupId: selection.dataGroupId,
            propertyDefaultType: selection.propertyDefaultType || propertyType,
            resourceType: selection.resourceType,
            type: propertyType,
            value: selection.propertyName,
          };
        }
        default:
          return null;
      }
    }

    private createPropertyFilter(attrs) {
      const displayContext = this.attr(`display-context`);
      return PropertyFilterFactory.createPropertyFilter(attrs, {displayContext});
    }

    private createCohortFilter(attrs) {
      return this.createPropertyFilter({
        ...attrs,
        ...(attrs.filterOperator ? {filterOperator: this.getSelectedCohortOperator(attrs.filterOperator)} : {}),
        propertyType: PropertyType.List,
        resourceType: ResourceType.Cohort,
      });
    }

    private getSelectedCohortOperator(selected: string) {
      return selected.endsWith(COHORT_FILTER_OPERATOR_LABELS[ListOperator.DoesNotContain])
        ? ListOperator.DoesNotContain
        : ListOperator.Contains;
    }

    private getCohortFilterOption(operator: ListOperator) {
      const dataGroupId = this.attr(`data-group-id`);
      const groupLabel = this.getCohortGroupLabel(dataGroupId);
      return `${groupLabel} ${COHORT_FILTER_OPERATOR_LABELS[operator]}`;
    }

    private getCohortFilterOptions() {
      return [
        this.getCohortFilterOption(ListOperator.Contains),
        this.getCohortFilterOption(ListOperator.DoesNotContain),
      ];
    }

    private getCohortGroupLabel(dataGroupId: string) {
      if (!dataGroupId) {
        return `Users`;
      } else {
        const dataGroup = this.state.dataGroups.find((group) => group.groupID === dataGroupId);
        return dataGroup ? `${dataGroup.displayName}(s)` : `Groups`;
      }
    }

    private trackingProps(item: PropertyFilter, rawItem: PropertyItem) {
      let extraProps = {};
      const isCohort = item.resourceType === ResourceType.Cohort;

      if (isCohort) {
        const cohortNames = (item.filterValue as CohortSegment[]).map((filter) => filter.cohort.name);
        extraProps = {
          // append cohort metadata
          ...(rawItem
            ? {
                'cohort visible': !rawItem.hidden,
                'cohort editable': !rawItem.locked,
                'cohort type': rawItem.createdBy
                  ? rawItem.createdBy.id === this.attr(`user-id`)
                    ? `cohorts-yours`
                    : `cohorts`
                  : `inline cohorts`,
              }
            : {}),
          'Filter value': cohortNames,
        };
      } else {
        extraProps = {
          'Filter value': item.filterValue,
        };
      }

      return {
        'Property name': item.propertyName,
        'Property data type': item.propertyType,
        'Resource type': item.resourceType,
        'Filter operator': item.filterOperator,
        ...extraProps,
      };
    }
  },
);
